import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import { Page, Title } from '../../components'
import GoogleSignInButton, { isGoogleSignInAvailable } from '../../containers/GoogleSignInButton'
import LanguageSelector from '../../containers/LanguageSelector'
import SignInForm from '../../containers/SignInForm'
import { urls } from '../../routing'
import { LiteralsService } from '../../services'
import {
  clearSignInFieldsAction,
  clearSignUpFieldsAction,
  clearVerificationFieldsAction,
  resetRecoverPasswordFieldsAction,
  setGuestModeAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'
import StyledArticle, { StyledClassicArticle } from './styles'
import { appsLogosSelected, appsStyleSelected, isClassicSignIn } from '../../helpers/app'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  language: string
}

// The form itself lives in SignInForm, shared with the prompt shown to guests.
class SignIn extends PureComponent<IProps> {
  public render(): ReactNode {
    if (isClassicSignIn) return this.classic

    return (
      <Page className={ `${appsStyleSelected?.style}` }>
        <StyledArticle>
          <div className="language-selector">
            <LanguageSelector />
          </div>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
              <h2>{ LiteralsService.get('signIn', true) }</h2>
            </header>
            { isGoogleSignInAvailable &&
              <section className="federated">
                <GoogleSignInButton />
                <div className="divider">
                  { LiteralsService.get('orWithYourAccount') }
                </div>
              </section>
            }
            <SignInForm
              onForgotPassword={ this.goToRecoverPassword }
              onVerification={ this.goToVerification }
              onSignUp={ this.goToSignUp }
              onGuest={ this.goToGuestMode }
            />
          </form>
        </StyledArticle>
      </Page>
    )
  }

  // The pre-redesign screen, for the brands that keep it (see appsSignInLayout).
  private get classic(): ReactNode {
    return (
      <Page className={ `${appsStyleSelected?.style}` }>
        <StyledClassicArticle>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
            </header>
            <Title text={ LiteralsService.get('signIn', true) }/>
            <SignInForm
              classic
              onForgotPassword={ this.goToRecoverPassword }
              onVerification={ this.goToVerification }
              onSignUp={ this.goToSignUp }
              onGuest={ this.goToGuestMode }
            />
          </form>
        </StyledClassicArticle>
      </Page>
    )
  }

  private goToRecoverPassword = () => {
    const { dispatch, history } = this.props

    dispatch(resetRecoverPasswordFieldsAction())
    history.push(urls.recoverPasswordRequestCodeStep)
  }

  private goToSignUp = () => {
    const { dispatch, history  } = this.props

    dispatch(clearSignUpFieldsAction())
    dispatch(clearSignInFieldsAction())
    history.push(urls.signUpStep1)
  }

  // An account that was created but never verified: the same screen the sign up ends in.
  private goToVerification = () => {
    const { dispatch, history } = this.props

    dispatch(clearVerificationFieldsAction())
    history.push(urls.accountVerificationStep)
  }

  private goToGuestMode = () => {
    const { dispatch, history } = this.props

    dispatch(setGuestModeAction())
    history.push(urls.newReservationBusStopsStep)
  }
}

// Redraws the literals when the language selector changes the language.
const mapStateToProps = ({ configuration }: IStoreState) => ({
  language: configuration.language
})

export default withRouter(connectRedux(mapStateToProps, SignIn))
